import { Input } from '@/app/components/Input'
import Modal, { ModalProps } from '@/app/components/Modal/Modal'
import { PrimaryButton } from '@/app/components/PrimaryButton'
import { SecondaryButton } from '@/app/components/SecondaryButton'
import { IManagerContact } from '@/types/email-name-pair.interface'
import {
  EnquiryFeature,
  IEnquiryCreatePayload,
  PaymentFeatureContext,
  PaymentMethodContext,
} from '@/types/enquiry'
import { CheckoutType, PaymentFeature } from '@/types/payment'
import { yupResolver } from '@hookform/resolvers/yup'
import { RichTranslationValues, useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
export enum EnquiryConfirmationModalCloseReason {
  CANCEL = 'CANCEL',
  CONFIRM = 'CONFIRM',
}

type EnquiryConfirmationModalProps = Omit<ModalProps, 'title' | 'onClose'> & {
  enquiry: IEnquiryCreatePayload | null
  onClose: (
    reason?: EnquiryConfirmationModalCloseReason,
    managerContact?: IManagerContact
  ) => Promise<void>
}

export function EnquiryConfirmationModal({
  enquiry,
  onClose,
  ...props
}: EnquiryConfirmationModalProps) {
  const [isLoading, setLoading] = useState(false)
  const t = useTranslations('Enquiries')

  const {
    register,
    setFocus,
    handleSubmit,
    formState: { errors },
  } = useForm<IManagerContact>({
    defaultValues: {
      email: '',
      role: '',
    },
    resolver: yupResolver(
      yup.object({
        email: yup.string().email().required(),
        role: yup.string().required(),
      })
    ),
  })

  useEffect(() => {
    if (props.isOpen) {
      setFocus('email')
    }
  }, [props.isOpen, setFocus])

  if (!enquiry) {
    return null
  }

  const DEFAULT_COMPONENTS: RichTranslationValues = {
    title: (chunks) => <b className="text-lg font-semibold">{chunks}</b>,
    b: (chunks) => <b className="font-medium">{chunks}</b>,
    ul: (chunks) => <ul className="mt-2 flex flex-col">{chunks}</ul>,
    li: (chunks) => (
      <li className="flex items-center gap-2">
        <p className="self-baseline">{chunks}</p>
      </li>
    ),
    p: () => <p>&nbsp;</p>,
  }

  const paymentMethodContext =
    enquiry.context && enquiry?.featureRequest === EnquiryFeature.PaymentMethod
      ? (enquiry.context as PaymentMethodContext)
      : null
  const paymentFeatureContext =
    enquiry?.featureRequest === EnquiryFeature.PaymentFeature
      ? (enquiry.context as PaymentFeatureContext)
      : null

  const featureName = paymentFeatureContext
    ? paymentFeatureContext?.paymentFeature
    : paymentMethodContext
      ? paymentMethodContext?.paymentMethod
      : ''

  const title =
    featureName === PaymentFeature.LearningBudget
      ? t('ask-payment-feature-learning-budget-enquiry-title')
      : featureName === CheckoutType.Fundae
        ? t('ask-payment-method-fundae-enquiry-title')
        : ''

  const body =
    featureName === PaymentFeature.LearningBudget
      ? [
          t.rich(
            'ask-payment-feature-learning-budget-enquiry-body',
            DEFAULT_COMPONENTS
          ),
          t.rich(
            'ask-payment-feature-learning-budget-enquiry-body2',
            DEFAULT_COMPONENTS
          ),
        ]
      : featureName === CheckoutType.Fundae
        ? [
            t.rich(
              'ask-payment-method-fundae-enquiry-body',
              DEFAULT_COMPONENTS
            ),
            t.rich(
              'ask-payment-method-fundae-enquiry-body2',
              DEFAULT_COMPONENTS
            ),
          ]
        : []

  const handleOnClose = async (
    reason: EnquiryConfirmationModalCloseReason = EnquiryConfirmationModalCloseReason.CANCEL,
    managerContact?: IManagerContact
  ) => {
    setLoading(true)
    try {
      await onClose(
        reason,
        reason === EnquiryConfirmationModalCloseReason.CONFIRM
          ? managerContact
          : undefined
      )
    } finally {
      setLoading(false)
    }
  }

  const handleConfirm = (managerContact: IManagerContact) => {
    handleOnClose(EnquiryConfirmationModalCloseReason.CONFIRM, managerContact)
  }

  const handleCancel = () => {
    handleOnClose()
  }

  return (
    <Modal
      {...props}
      noCloseButton
      onClose={handleOnClose}
      title={title}
      className="md:max-w-[664px]"
    >
      <form onSubmit={handleSubmit(handleConfirm)}>
        <div className="flex flex-col gap-4 p-8 pt-0">
          <div>{body[0]}</div>
          <Input
            errorMessage={errors.email?.message}
            placeholder="email@company.com"
            {...register('email')}
          />
          <Input
            errorMessage={errors.role?.message}
            placeholder="Job Title"
            {...register('role')}
          />
          <div>{body[1]}</div>
        </div>
        <hr />
        <div className="flex justify-end gap-2 p-5">
          <SecondaryButton
            disabled={isLoading}
            type="button"
            onClick={handleCancel}
          >
            {t('back')}
          </SecondaryButton>

          <PrimaryButton isLoading={isLoading} type="submit">
            {t('continue')}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  )
}
